import React, { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * LabBOXX is used ONLY on a tenant's own address (owner, 2026-10-04: a login
 * exists only on a valid tenant address).
 *
 * labboxx.com itself names no tenant, yet it drew the LIMS sign-in and the
 * server checked passwords against the central registry. The server now
 * refuses sign-in there (shared/middleware/tenant_middleware.py
 * NO_TENANT_SIGNIN_PATHS); this side draws LabBOXX's own landing page there
 * instead of any screen (owner, 2026-10-05) — it used to hand over to
 * saas-boxx.com/product/labboxx, which is no longer open to the public.
 * Nothing in the LIMS works without a tenant, so every path draws it, not
 * just /login. /verify-email stays: it is a legacy prospect e-mail link, not
 * a sign-in. enterprise.labboxx.com (platform staff), localhost and LAN are
 * untouched.
 */
const ROOT_DOMAIN = 'labboxx.com';
// Same hosts as the server's NO_TENANT_SIGNIN_HOST_LABELS.
const NOT_TENANTS = new Set(['www', 'api']);
const STILL_SERVED = ['/verify-email'];

// Only an address that names no tenant draws it — kept out of the LIMS bundle.
const LabLanding = lazy(() => import('@/pages/LabLanding'));

export function isNoTenantAddress(host: string = window.location.hostname): boolean {
  const h = host.toLowerCase();
  if (h === ROOT_DOMAIN) return true;
  if (!h.endsWith(`.${ROOT_DOMAIN}`)) return false;
  return NOT_TENANTS.has(h.slice(0, -(ROOT_DOMAIN.length + 1)));
}

/** Must sit inside the router: it re-checks on every navigation. */
export function NoAppWithoutTenant({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  if (isNoTenantAddress() && !STILL_SERVED.some((p) => pathname.startsWith(p))) {
    return <Suspense fallback={null}><LabLanding /></Suspense>;
  }
  return <>{children}</>;
}
